import type { LabelCustomOffset, Marker } from '@tencent/chart/lib/antv/g2/interface';
import type { Trendline } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/properties/trendline';
import type { IChartLabel } from '@tencent/chart/lib/chart-ui/interface';
import type { SubChartType, STBarGrouping, STDLblPos, STGrouping } from '@tencent/slide-sdk-model';
import { IBackgroundFillInfo } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { IEffectModelValCollection } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { IEffect, IFillStyle, IStrokeStyle } from '@tencent/alloy-graphics';
import { EditorValue } from '@tencent/docs-editor/lib/types/interfaces/element';

// 以下为图表框架所用的数据结构

// 将复杂chartModel简化过的数据，方便子类方法、直接使用
export interface BaseChartData {
    type: SubChartType;
    data: Array<ChartUIDataItem>;
    series: Array<ChartUISeries>;
    grouping?: STBarGrouping | STGrouping;
}

export interface ShapeOriginStyle {
    effects?: IEffectModelValCollection;  // 原始效果数据
    outline?: IBorderInfo;  // 原始边框数据
    background?: IBackgroundFillInfo; // 原始背景数据
}

export interface GeometryShape extends ShapeOriginStyle {
    fill: string;
    border?: ShapeStyle
}

/**
 * 自定义渲染样式集。传递给alloy-graphic使用
 */
export interface CustomStyles {
    effects?: IEffect;  // 特效
    strokeStyle?: IStrokeStyle;  // 边框
    fillStyle?: IFillStyle; // 填充
    text?: EditorValue;  // 文本
}

export interface Text {
    text?: string;
    style: GeometryShape & {
        fontSize: number;
        fontStyle?: string;
        fontWeight?: string | number;
        textAlign?: string;
    };
}
// G2使用的一维数组数据源
export interface ChartUIDataItem extends GeometryShape {
    id: string;
    category: string | number;
    categoryIndex: number;  // 用于趋势线转换x轴坐标
    name: string;
    serIndex: number;
    value: number;
    type: SubChartType;
    label: ChartUILabel | boolean;
    marker?: ChartUIMarker;
    dataPointStyle?: GeometryShape;
    // 用于在data中区分是否为趋势线。目前用于选中line后，需要根据是否为趋势线做锚点的一些特殊逻辑
    isTrendLine?: boolean;
    dataId: string;
    seriesId: string;
    originValue: string | number | undefined;
    originCategory: number | string | undefined;
}

export interface ChartUILabel extends Text {
    id?: string;
    position: string;
    originPosition?: STDLblPos; // 用于记录Model上的position，用于二次判定。因为g2的position已经转换过，可能是多对一
    labelLine?: boolean;
    content: string;
    customOffset: LabelCustomOffset; // 自定义布局信息，由layout转化
    autoRotate?: boolean;
}
// 用于g2 color视觉通道callback的数据
export interface ChartUISeries extends GeometryShape {
    name: string;
    index: number;
    values?: Array<string | number | undefined>;
    categories?: Array<string | number | undefined>;
    type: SubChartType;
    trendlines?: Trendline[];
    marker?: ChartUIMarker;
    disable?: boolean;
    seriesId: string;
}

export interface ChartUIMarker extends GeometryShape {
    size?: number;
    symbol: Marker;
}

export interface GeometryStyle {
    fill?: string;
    fillOpacity?: number;
    stroke?: string;
    strokeOpacity?: number;
    shadowColor?: string;
    shadowBlur?: number;
    shadowOffsetX?: number;
    shadowOffsetY?: number;
    opacity?: number;
}
export interface LineStyle extends ShapeOriginStyle{
    lineCap?: 'butt' | 'round' | 'square';
    lineJoin?: 'bevel' | 'round' | 'miter';
    lineWidth?: number;
    miterLimit?: number;
    lineDash?: Array<number>;
    originLineWidth?: number;
}
export interface TextStyle {
    // 设置文本内容的当前对齐方式, 支持的属性：'start', 'center', 'end', 'left', 'right'
    textAlign?: 'start' | 'center' | 'end' | 'left' | 'right';
    // 设置在绘制文本时使用的当前文本基线, 支持的属性：'top', 'hanging', 'middle', 'alphabetic', 'ideographic', 'bottom'
    textBaseline?: 'top' | 'hanging' | 'middle' | 'alphabetic' | 'ideographic' | 'bottom';
    fontStyle?: 'normal' | 'italic' | 'oblique'; // 规定字体样式。可能的值：'normal', 'italic', 'oblique'
    fontSize?: number; // 规定字号，以像素计
    fontFamily?: string; // 规定字体系列
    // 规定字体的粗细。可能的值：'normal', 'bold', 'bolder', 'lighter', '100', '200, '300', '400','500', '600', '700', '800', '900'
    fontWeight?: string;
    fontVariant?: string; // 规定字体变体。可能的值：'normal', 'small-caps'
    lineHeight?: number; // 规定行高，以像素
}

export type ShapeStyle = GeometryStyle & LineStyle & { customStyles?: CustomStyles };

export interface LooseObject {
    [key: string]: any;
}

export type ChartPadding = [number, number, number, number];

export type GemLabelConf = IChartLabel | boolean;

export const EMPTY_OPTIONS = undefined;

export type IValueAxisScale = {
    min: number,
    max: number,
    majorUnit: number;
    minorUnit: number;
};

export enum ChartUILabelPosition {
    Center = 'middle',
    Right = 'right',
    Left = 'left',
    Top = 'top',
    Bottom = 'bottom',
    InEnd = 'inEnd',
    OutEnd = 'outEnd',
    InBase = 'inBase',
}

export interface ChartUILabelOffset {
    offsetX?: number;
    offsetY?: number;
    offset?: number | string;
}

export interface ChartUIFrame {
    left: number,
    top: number,
    width: number,
    height: number,
}

export interface ILegendGrid {
    totalCount: number,
    columnCount: number,
    rowCount: number,
}

export interface ILegendMeasureInfo {
    itemHeightList: number[], // 存储每一项item高度, 用来计算整体item height
    itemWidthList: number[], // 存储每一项item宽度, 超过最大宽度时为最大宽度
    itemLayoutNameList: string[], // 存储每一项item高度, 用来计算整体item height
}

export interface ILegendMarkerSize {
    maxMarkerWidth: number,
    maxMarkerHeight: number,
    markerSpacing: number,
    markerSizeList: { width: number, height: number }[],
}

export interface ILegendItemSize {
    itemWidth?: number, // item width为空时，让G2自己排版
    itemHeight: number,
    maxItemWidth: number,
    maxItemHeight: number,
    itemSpacing: number,
    itemMarginBottom: number,
}

export interface ILegendUISize extends ILegendItemSize, ChartUIFrame {
    offsetX: number;
    offsetY: number;
    paddingTB: number,
    paddingLR: number,
    marginTop: number,
    marginBottom: number,
    marginLeft: number,
    marginRight: number,
}

/**
 * 图例marker类型
 */
export enum MarkerType {
    MinSquare = 'minSquare',
    Square = 'square',
    Rect = 'rect', // 矩形
    Line = 'hor-line', // 横线
    Circle = 'circle',
}
